import React, { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { createQuote, sendQuote, fmt } from "../lib/api";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";

export const QuoteDialog = ({ open, onOpenChange, lines, netTotal, vatRate, vatAmount, total, onSent, projectId }) => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    address: "",
    zip_code: "",
    province: "",
    email: "",
    phone: "",
    start_period: "Entro 3 mesi",
    notes: "",
  });
  const [privacy, setPrivacy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const missing = [
      "first_name",
      "last_name",
      "address",
      "zip_code",
      "province",
      "email",
      "phone",
    ].filter((k) => !form[k].trim());
    if (missing.length) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    if (!privacy) {
      toast.error("Devi accettare il trattamento dei dati");
      return;
    }
    setBusy(true);
    let quoteId = null;
    try {
      const quote = await createQuote({
        ...form,
        lines,
        privacy_consent: privacy,
        recontact_consent: privacy,
        project_id: projectId || null,
        vat_rate: vatRate,
      });
      quoteId = quote.id;
      await sendQuote(quote.id);
      setDone(true);
      onSent?.({
        name: `${form.first_name} ${form.last_name}`.trim(),
        email: form.email,
        phone: form.phone,
        notify: privacy,
      });
      toast.success("Preventivo inviato alla tua email");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (quoteId) {
        toast.error(
          detail
            ? `${detail}. La tua richiesta è stata salvata, ti contatteremo.`
            : "Invio email non riuscito. La tua richiesta è stata salvata, ti contatteremo."
        );
      } else {
        toast.error(detail || "Invio non riuscito, controlla i dati inseriti");
      }
    } finally {
      setBusy(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setDone(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto rounded-none sm:max-w-md"
        data-testid="quote-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">
            {done ? "Preventivo inviato" : "Ricevi il preventivo"}
          </DialogTitle>
        </DialogHeader>
        {done ? (
          <div className="space-y-4 py-4 text-center" data-testid="quote-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-[#059669]" />
            <p className="text-sm text-muted-foreground">
              Abbiamo inviato la stima di <strong>€ {fmt(total)}</strong> a {form.email}.
            </p>
            <Button
              data-testid="quote-close-btn"
              onClick={() => close(false)}
              className="w-full rounded-none"
            >
              Chiudi
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-xs text-muted-foreground">Nome *</label>
                <Input
                  data-testid="input-quote-name"
                  className="mt-1 rounded-none"
                  value={form.first_name}
                  onChange={set("first_name")}
                />
              </div>
              <div>
                <label className="label-xs text-muted-foreground">Cognome *</label>
                <Input
                  data-testid="input-quote-lastname"
                  className="mt-1 rounded-none"
                  value={form.last_name}
                  onChange={set("last_name")}
                />
              </div>
            </div>
            <div>
              <label className="label-xs text-muted-foreground">Via e città *</label>
              <Input
                data-testid="input-quote-address"
                className="mt-1 rounded-none"
                placeholder="Via Roma 12, Milano"
                value={form.address}
                onChange={set("address")}
              />
            </div>
            <div>
              <label className="label-xs text-muted-foreground">Email *</label>
              <Input
                data-testid="input-quote-user-email"
                type="email"
                className="mt-1 rounded-none"
                value={form.email}
                onChange={set("email")}
              />
            </div>
            <div>
              <label className="label-xs text-muted-foreground">Telefono *</label>
              <Input
                data-testid="input-quote-phone"
                className="mt-1 rounded-none"
                value={form.phone}
                onChange={set("phone")}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-xs text-muted-foreground">CAP *</label>
                <Input
                  data-testid="input-quote-zip"
                  className="mt-1 rounded-none"
                  placeholder="20121"
                  value={form.zip_code}
                  onChange={set("zip_code")}
                />
              </div>
              <div>
                <label className="label-xs text-muted-foreground">Provincia *</label>
                <Input
                  data-testid="input-quote-province"
                  className="mt-1 rounded-none"
                  placeholder="MI"
                  maxLength={2}
                  value={form.province}
                  onChange={set("province")}
                />
              </div>
            </div>
            <div>
              <label className="label-xs text-muted-foreground">Inizio lavori desiderato *</label>
              <select
                data-testid="input-quote-start-period"
                value={form.start_period}
                onChange={set("start_period")}
                className="mt-1 w-full border border-border bg-background px-3 py-2 text-sm outline-none focus:border-[#4E7C59]"
              >
                <option>Il prima possibile</option>
                <option>Entro 3 mesi</option>
                <option>Entro 6 mesi</option>
                <option>Oltre 6 mesi</option>
                <option>Solo informativo</option>
              </select>
            </div>
            <div>
              <label className="label-xs text-muted-foreground">Note (opz.)</label>
              <Textarea
                data-testid="input-quote-notes"
                className="mt-1 rounded-none"
                rows={2}
                value={form.notes}
                onChange={set("notes")}
              />
            </div>
            <label className="flex items-start gap-2 border-t border-border pt-3 text-xs text-muted-foreground">
              <input
                data-testid="input-quote-privacy"
                type="checkbox"
                checked={privacy}
                onChange={(e) => setPrivacy(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#4E7C59]"
              />
              <span>
                Dichiaro di aver letto l’informativa privacy e autorizzo il trattamento dei dati
                necessario alla gestione, all’invio del preventivo e agli eventuali contatti
                relativi alla richiesta effettuata. *
              </span>
            </label>
            <div className="space-y-1 border-t border-border pt-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="label-xs text-muted-foreground">Totale lavori netto</span>
                <span className="font-mono-t">€ {fmt(netTotal)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="label-xs text-muted-foreground">IVA {vatRate}%</span>
                <span className="font-mono-t">€ {fmt(vatAmount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="label-xs text-muted-foreground">Totale stimato</span>
                <span className="font-mono-t text-xl font-bold text-[#4E7C59]">€ {fmt(total)}</span>
              </div>
            </div>
            <Button
              data-testid="btn-send-quote-email"
              type="submit"
              disabled={busy}
              className="w-full rounded-none bg-[#1B2A20] text-[#F4F7F2] hover:bg-[#4E7C59]"
            >
              {busy ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Mail className="mr-2 h-4 w-4" />
              )}
              Invia preventivo via email
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
